import * as React from 'react';
import { ViewSettings, ToolType, SymbolType, SystemType, Point } from '@shared/types/tsk';
import GridLayer from './Editor/Layers/GridLayer';
import PipeLayer from './Editor/Layers/PipeLayer';
import SymbolLayer from './Editor/Layers/SymbolLayer';
import SelectionMenu from './Editor/SelectionMenu';
import TradeIcon from './TradeIcon';
import Toolbar from './Toolbar';
import ViewSettingsPanel from './ViewSettingsPanel';
import GridSettingsPanel from './GridSettingsPanel';
import PlanReview from './PlanReview';
import { Check, Trash2, X, Undo2, Redo2, Eye, Hash, Crop } from 'lucide-react';
import { SYSTEM_COLORS } from '../utils/drawing';

export const renderEditorView = (props: any) => {
  return (
    <div
      id="trade-sketch-canvas"
      ref={props.containerRef}
      className="w-full h-full relative overflow-hidden bg-[var(--color-background)] touch-none cursor-crosshair"
      onPointerDown={props.handlePointerDown}
      onPointerMove={props.handlePointerMove}
      onPointerUp={props.handlePointerUp}
      onPointerCancel={props.handlePointerUp}
      onPointerLeave={() => { props.setCursorPos(null); props.setIsSnappedToSymbol(null); }}
      onWheel={props.handleWheel}
    >
      <div style={{ transform: `translate(${props.offset.x}px, ${props.offset.y}px) scale(${props.scale})`, transformOrigin: '0 0' }} className="absolute top-0 left-0 pointer-events-none">
        <div className="relative">
          <img ref={props.imgRef} src={props.image} alt="Plan" className="max-w-none block" />
          <GridLayer worldSize={props.worldSize} gridSize={props.viewSettings.gridSize} showGrid={props.viewSettings.showGrid} />
        </div>
        <PipeLayer worldSize={props.worldSize} lines={props.markup.lines} activeTool={props.activeTool} activeSystem={props.activeSystem} selection={props.selection} activeGrip={props.activeGrip} draggingGripId={props.draggingGripId} scale={props.scale} lineBuilding={props.lineBuilding} currentCursorWorld={props.cursorPos?.world || null} isSnappedToSymbol={props.isSnappedToSymbol} />
        <SymbolLayer worldSize={props.worldSize} symbols={props.markup.symbols} scale={props.scale} selection={props.selection} activeTool={props.activeTool} currentPipeWidth={props.currentPipeWidth} />
      </div>

      {props.isCropMode && props.cropRect && (
        <div className="absolute inset-0 pointer-events-none z-30 overflow-hidden bg-black/60">
          <div style={{
            position: 'absolute',
            left: Math.min(props.cropRect.startWorld.x, props.cropRect.endWorld.x) * props.scale + props.offset.x,
            top: Math.min(props.cropRect.startWorld.y, props.cropRect.endWorld.y) * props.scale + props.offset.y,
            width: Math.abs(props.cropRect.endWorld.x - props.cropRect.startWorld.x) * props.scale,
            height: Math.abs(props.cropRect.endWorld.y - props.cropRect.startWorld.y) * props.scale,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
            background: 'transparent'
          }} className="border border-white ring-1 ring-black/50 pointer-events-auto cursor-crosshair touch-none" onPointerDown={props.handleCropPointerDown} onPointerMove={props.handleCropPointerMove} onPointerUp={props.handleCropPointerUp} onPointerCancel={props.handleCropPointerUp} />
          
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-4 z-40 bg-[#0a0a0b]/90 backdrop-blur-md p-4 rounded-3xl border border-white/5 pointer-events-auto">
            <button onClick={props.cancelCrop} className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/5 text-white/50 hover:bg-white/10 hover:text-white transition-all"><X size={20} /></button>
            <button onClick={props.confirmCrop} className="px-6 h-12 flex items-center gap-2 rounded-2xl bg-emerald-500 text-black font-black uppercase tracking-widest text-[10px] hover:bg-emerald-400 transition-all shadow-lg"><Check size={18} /><span>Crop</span></button>
          </div>
        </div>
      )}

      {props.floatingPos && (
        <SelectionMenu
          floatingPos={props.floatingPos}
          selection={props.selection}
          liveSelectionData={props.liveSelectionData}
          changeSelectedSystem={props.changeSelectedSystem}
          changeSelectedSymbolType={props.changeSelectedSymbolType}
          deleteSelected={props.deleteSelected}
          availableFixtures={props.availableFixtures}
          availableFittings={props.availableFittings}
          availableDirections={props.availableDirections}
        />
      )}

      {props.cursorPos && props.activeTool !== ToolType.PAN && (
        <div style={{ left: props.cursorPos.screen.x, top: props.cursorPos.screen.y, transform: 'translate(-50%, -50%)', width: Math.max(24, props.currentIconSize), height: Math.max(24, props.currentIconSize) }} className="absolute pointer-events-none z-50">
          {props.activeTool === ToolType.SYMBOL ? (
            <div className="w-full h-full flex items-center justify-center bg-[var(--color-primary)]/10 rounded-full border border-[var(--color-primary)]/40 shadow-[0_0_15px_rgba(255,107,0,0.2)]">
              <TradeIcon type={props.activeSymbol} color="var(--color-primary)" size={Math.max(16, props.currentIconSize * 0.7)} strokeWidth={1.5} />
            </div>
          ) : props.activeTool === ToolType.PIPE && props.lineBuilding && (
            <div className="w-3 h-3 rounded-full border-2 bg-[var(--color-background)]" style={{ borderColor: SYSTEM_COLORS[props.activeSystem] }} />
          )}
        </div>
      )}

      {props.lineBuilding && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 bg-[var(--color-primary-orange-dark)]/90 backdrop-blur-md text-white px-6 py-3 rounded-2xl font-black text-xs shadow-2xl z-50 pointer-events-auto border border-white/10 uppercase tracking-widest flex items-center gap-4">
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-white animate-pulse" /><span>Drawing Pipe</span></div>
          <button onClick={props.finishActivePipe} onPointerDown={props.stopBubbling} className="flex items-center gap-1.5 bg-black/30 hover:bg-black/50 transition-colors px-3 py-1.5 rounded-xl text-white/90"><Check size={14} /><span>Finish</span></button>
        </div>
      )}

      <div className="absolute top-24 left-6 z-40 pointer-events-auto max-h-[calc(100vh-200px)] overflow-hidden flex">
        <Toolbar activeTool={props.activeTool} setActiveTool={props.setActiveTool} activeSymbol={props.activeSymbol} setActiveSymbol={props.setActiveSymbol} activeSystem={props.activeSystem} setActiveSystem={props.setActiveSystem} />
      </div>

      <div className="absolute bottom-6 left-6 z-40 flex gap-4 pointer-events-auto">
        <div className="flex bg-[#0a0a0b]/80 backdrop-blur-md p-2 rounded-3xl shadow-xl border border-white/5">
          <button onClick={props.undo} disabled={!props.canUndo} className="w-12 h-12 flex items-center justify-center rounded-2xl transition-all disabled:opacity-20 disabled:cursor-not-allowed hover:bg-white/10 text-[var(--color-primary-gray)] hover:text-white"><Undo2 size={20} /></button>
          <div className="w-px bg-white/10 my-2 mx-1" />
          <button onClick={props.redo} disabled={!props.canRedo} className="w-12 h-12 flex items-center justify-center rounded-2xl transition-all disabled:opacity-20 disabled:cursor-not-allowed hover:bg-white/10 text-[var(--color-primary-gray)] hover:text-white"><Redo2 size={20} /></button>
        </div>
        <div className="flex bg-[#0a0a0b]/80 backdrop-blur-md p-2 rounded-3xl shadow-xl border border-white/5">
          <button onClick={() => props.setIsViewSettingsOpen(true)} className="w-12 h-12 flex items-center justify-center rounded-2xl transition-all hover:bg-white/10 text-[var(--color-primary-gray)] hover:text-white"><Eye size={20} /></button>
          <div className="w-px bg-white/10 my-2 mx-1" />
          <button onClick={() => props.setIsGridSettingsOpen(true)} className="w-12 h-12 flex items-center justify-center rounded-2xl transition-all hover:bg-white/10 text-[var(--color-primary-gray)] hover:text-white"><Hash size={20} /></button>
          <div className="w-px bg-white/10 my-2 mx-1" />
          <button onClick={() => { props.setActiveTool(ToolType.PAN); props.setIsCropMode(true); }} className="w-12 h-12 flex items-center justify-center rounded-2xl transition-all hover:bg-white/10 text-[var(--color-primary-gray)] hover:text-white"><Crop size={20} /></button>
        </div>
      </div>

      <PlanReview compliance={props.compliance} />

      <ViewSettingsPanel isOpen={props.isViewSettingsOpen} onClose={() => props.setIsViewSettingsOpen(false)} settings={props.viewSettings} onSettingsChange={props.setViewSettings} />
      <GridSettingsPanel isOpen={props.isGridSettingsOpen} onClose={() => props.setIsGridSettingsOpen(false)} settings={props.viewSettings} onSettingsChange={props.setViewSettings} />
    </div>
  );
};
